/* Iron+Steel v3 collection listing: AJAX filters/sort, six-rows-at-a-time loading, favorites, quick view. */




(() => {
  if (customElements.get('is-collection')) return;

  class ISCollection extends HTMLElement {
    connectedCallback() {
      this.rows = parseInt(this.dataset.rows, 10) || 6;
      this.onPop = () => this.load(window.location.href, false);
      window.addEventListener('popstate', this.onPop);
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPop);
      if (this.observer) this.observer.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    init() {
      this.form = this.querySelector('[data-filter-form]');
      this.grid = this.querySelector('[data-grid]');
      this.more = this.querySelector('[data-more]');
      this.moreLine = this.querySelector('[data-more-line]');
      this.nextLink = this.querySelector('[data-next-page]');
      this.nextUrl = this.nextLink ? this.nextLink.dataset.nextPage : null;
      this.total = parseInt((this.querySelector('[data-result-line]')?.textContent || '').trim(), 10) || 0;
      this.fetching = false;

      this.bindFilters();
      if (this.grid) {
        this.bindCards(this.grid.querySelectorAll('.is-gcard'));
        this.setupBatches();
      }
    }

    /* ---------- Filters and sort ---------- */
    bindFilters() {
      if (!this.form) return;

      // Phone toolbar: "Filter" and "Sort" reveal the pills and the sort row. Only one at a
      // time, and opening one closes any filter panel left open under the other.
      const mToggles = Array.from(this.querySelectorAll('[data-m-toggle]'));
      mToggles.forEach((btn) => {
        btn.addEventListener('click', () => {
          const panel = this.querySelector(`[data-m-panel="${btn.dataset.mToggle}"]`);
          if (!panel) return;
          const open = btn.getAttribute('aria-expanded') === 'true';
          mToggles.forEach((b) => {
            b.setAttribute('aria-expanded', 'false');
            const p = this.querySelector(`[data-m-panel="${b.dataset.mToggle}"]`);
            if (p) p.classList.remove('is-open');
          });
          if (!open) {
            btn.setAttribute('aria-expanded', 'true');
            panel.classList.add('is-open');
          }
          this.openMobilePanel = open ? null : btn.dataset.mToggle;
        });
      });

      const toggles = Array.from(this.querySelectorAll('[data-group-toggle]'));
      toggles.forEach((btn) => {
        btn.addEventListener('click', () => {
          const panel = document.getElementById(btn.getAttribute('aria-controls'));
          const open = btn.getAttribute('aria-expanded') === 'true';
          toggles.forEach((b) => {
            b.setAttribute('aria-expanded', 'false');
            document.getElementById(b.getAttribute('aria-controls')).hidden = true;
          });
          if (!open) {
            btn.setAttribute('aria-expanded', 'true');
            panel.hidden = false;
          }
          this.openPanelId = open ? null : panel.id;
        });
      });

      this.form.addEventListener('change', (e) => {
        if (e.target.type === 'number' || e.target.matches('[data-live-input]')) return;
        this.submitForm();
      });
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        clearTimeout(this.liveTimer);
        this.submitForm();
      });
      // Search page: results follow the box as you type.
      const live = this.form.querySelector('[data-live-input]');
      if (live) {
        live.addEventListener('input', () => {
          clearTimeout(this.liveTimer);
          this.liveTimer = setTimeout(() => this.submitForm(false), 350);
        });
      }
      this.querySelectorAll('[data-filter-link]').forEach((a) =>
        a.addEventListener('click', (e) => {
          e.preventDefault();
          this.load(a.href, true);
        })
      );
    }

    submitForm(push = true) {
      const params = new URLSearchParams();
      new FormData(this.form).forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });
      this.load(`${this.dataset.baseUrl}?${params.toString()}`, push, !push);
    }

    async load(url, push, replace) {
      const token = (this.loadToken = (this.loadToken || 0) + 1);
      this.setAttribute('aria-busy', 'true');
      try {
        const fetchUrl = new URL(url, window.location.origin);
        fetchUrl.searchParams.set('section_id', this.dataset.sectionId);
        const res = await fetch(fetchUrl.toString());
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        if (token !== this.loadToken) return; // a newer request superseded this one
        const fresh = doc.querySelector('is-collection');
        if (!fresh) throw new Error('No section');
        const keepOpen = this.openPanelId;
        const keepMobile = this.openMobilePanel;
        // Keep the caret and anything typed since the request went out.
        const active = document.activeElement;
        const focus = active && this.contains(active) && active.id && active.matches('input')
          ? {id: active.id, value: active.value, pos: active.selectionStart}
          : null;
        this.innerHTML = fresh.innerHTML;
        if (push) window.history.pushState({}, '', url);
        else if (replace) window.history.replaceState({}, '', url);
        this.init();
        if (focus) {
          const el = document.getElementById(focus.id);
          if (el) {
            el.value = focus.value;
            el.focus();
            try { el.setSelectionRange(focus.pos, focus.pos); } catch (e) { /* type=search may not support it */ }
          }
        }
        if (keepMobile) {
          // A tap on a checkbox re-renders the section; leave the phone sheet where it was.
          const panel = this.querySelector(`[data-m-panel="${keepMobile}"]`);
          const btn = this.querySelector(`[data-m-toggle="${keepMobile}"]`);
          if (panel && btn) {
            panel.classList.add('is-open');
            btn.setAttribute('aria-expanded', 'true');
            this.openMobilePanel = keepMobile;
          }
        }
        if (keepOpen) {
          const panel = document.getElementById(keepOpen);
          const btn = this.querySelector(`[aria-controls="${keepOpen}"]`);
          if (panel && btn) {
            panel.hidden = false;
            btn.setAttribute('aria-expanded', 'true');
            this.openPanelId = keepOpen;
          }
        }
      } catch (err) {
        window.location.href = url;
      }
      this.removeAttribute('aria-busy');
    }

    /* ---------- Six rows at a time ---------- */
    columns() {
      const cols = getComputedStyle(this.grid).gridTemplateColumns.split(' ').filter(Boolean).length;
      return Math.max(1, cols);
    }

    batchSize() {
      return this.columns() * this.rows;
    }

    cards() {
      return Array.from(this.grid.children);
    }

    setupBatches() {
      const batch = this.batchSize();
      this.cards().forEach((card, i) => { card.hidden = i >= batch; });

      if (this.nextLink) {
        this.nextLink.addEventListener('click', (e) => {
          e.preventDefault();
          this.revealNext();
        });
      }

      if (this.observer) this.observer.disconnect();
      this.observer = new IntersectionObserver(
        (entries) => { if (entries.some((en) => en.isIntersecting)) this.revealNext(); },
        {rootMargin: '0px 0px 600px 0px'}
      );
      if (this.more) this.observer.observe(this.more);

      // Keep whole rows when the column count changes.
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.lastCols = this.columns();
      this.resizeObserver = new ResizeObserver(() => {
        const cols = this.columns();
        if (cols === this.lastCols) return;
        this.lastCols = cols;
        const shown = this.cards().filter((c) => !c.hidden).length;
        const target = Math.ceil(shown / cols) * cols;
        this.cards().forEach((card, i) => { card.hidden = i >= target; });
        this.updateLine();
      });
      this.resizeObserver.observe(this.grid);

      this.updateLine();
    }

    async revealNext() {
      if (this.revealing) return;
      this.revealing = true;
      const batch = this.batchSize();
      let hidden = this.cards().filter((c) => c.hidden);

      // Pull more pages in the background until a full batch is buffered (or the collection ends).
      while (hidden.length < batch && this.nextUrl) {
        await this.fetchNextPage();
        hidden = this.cards().filter((c) => c.hidden);
      }

      hidden.slice(0, batch).forEach((c) => { c.hidden = false; });
      this.updateLine();
      this.revealing = false;

      // If the sentinel is still on screen (tall viewport), keep going.
      if (this.more && this.cards().some((c) => c.hidden || this.nextUrl)) {
        const rect = this.more.getBoundingClientRect();
        if (rect.top < window.innerHeight + 600 && this.cards().some((c) => c.hidden)) this.revealNext();
      }
    }

    async fetchNextPage() {
      if (!this.nextUrl || this.fetching) return;
      this.fetching = true;
      try {
        const url = new URL(this.nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);
        const res = await fetch(url.toString());
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const newCards = Array.from(doc.querySelectorAll('[data-grid] > .is-gcard'));
        newCards.forEach((card) => {
          card.hidden = true;
          this.grid.appendChild(card);
        });
        this.bindCards(newCards);
        const next = doc.querySelector('[data-next-page]');
        this.nextUrl = next ? next.dataset.nextPage : null;
      } catch (err) {
        this.nextUrl = null;
      }
      this.fetching = false;
    }

    noun() {
      return this.dataset.noun || 'product';
    }

    updateLine() {
      const shown = this.cards().filter((c) => !c.hidden).length;
      const done = !this.nextUrl && !this.cards().some((c) => c.hidden);
      if (this.moreLine) {
        this.moreLine.textContent = done
          ? `Showing all ${shown} ${shown === 1 ? this.noun() : `${this.noun()}s`}`
          : `Showing ${shown} of ${this.total || shown}`;
      }
      if (this.nextLink) this.nextLink.hidden = done;
    }

    /* ---------- Cards: favorites + quick view ---------- */
    bindCards(cards) {
      cards.forEach((card) => {
        const handle = card.dataset.handle;
        const fav = card.querySelector('[data-card-fav]');
        if (fav && window.ISWishlist) {
          const sync = () => {
            const on = window.ISWishlist.read().includes(handle);
            fav.setAttribute('aria-pressed', on ? 'true' : 'false');
          };
          fav.addEventListener('click', () => {
            const list = window.ISWishlist.read();
            window.ISWishlist.write(list.includes(handle) ? list.filter((h) => h !== handle) : [...list, handle]);
            sync();
          });
          document.addEventListener('is:wishlist:change', sync);
          sync();
        }

        const plus = card.querySelector('[data-quick-view]');
        if (plus) plus.addEventListener('click', () => window.ISQuickView.open(plus.dataset.quickView, plus));
      });
    }
  }

  customElements.define('is-collection', ISCollection);
})();
